import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import { EventClickArg, DateSelectArg } from '@fullcalendar/core';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { differenceInDays } from 'date-fns';
import { ZoomIn, ZoomOut } from 'lucide-react';
import { useIntl } from 'react-intl';
import { Button } from '../../../../ui/button';
import { Segmented } from '../../../../ui/segmented';
import { Alert } from '../../../../ui/alert';

import { activityStore } from '../../../../../store/activity.store';
import { Interval } from '../../../../../model/activity.model';
import { ActivityStatus } from '../../../../../enum/activity.enum';
import convertDecimalToHexColor from '../../../../../utils/convertDecimalToHexColor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../ui/alert-dialog';
import './FullCalendarView.css';
import EditModeBar from './EditModeBar';
import EventContextMenu from './EventContextMenu';
import SlotContextMenu from './SlotContextMenu';

// Constants
const MAX_CALENDAR_DAYS = 7;
const NEARBY_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
const MENU_TOGGLE_DELAY_MS = 100;

// Helper to adjust menu position to stay within viewport
const adjustMenuPosition = (x: number, y: number, menuWidth = 200, menuHeight = 150) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const padding = 8;

  let adjustedX = x;
  let adjustedY = y;

  // Adjust X if menu would overflow right edge
  if (x + menuWidth + padding > viewportWidth) {
    adjustedX = viewportWidth - menuWidth - padding;
  }

  // Adjust Y if menu would overflow bottom edge
  if (y + menuHeight + padding > viewportHeight) {
    adjustedY = viewportHeight - menuHeight - padding;
  }

  return { x: Math.max(padding, adjustedX), y: Math.max(padding, adjustedY) };
};

// Synthetic duration for occurrence events (start === finish) so they're visible on the calendar
const OCCURRENCE_SYNTHETIC_DURATION_MS = 5 * 60 * 1000; // 5 minutes

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  backgroundColor: string;
  borderColor: string;
  classNames?: string[];
  startEditable?: boolean;
  durationEditable?: boolean;
  extendedProps: {
    interval: Interval;
    isOccurrence?: boolean;
  };
}

interface ContextMenuState {
  x: number;
  y: number;
  interval: Interval | null;
}

interface SlotContextMenuState {
  x: number;
  y: number;
  start: Date | null;
  end: Date | null;
  gapStart: Date | null;
  gapEnd: Date | null;
  hasGap: boolean;
  isClick: boolean;
  prevEventEnd: Date | null;
  prevEventName: string | null;
  prevEventNearby: boolean;
  nextEventStart: Date | null;
  nextEventName: string | null;
  nextEventNearby: boolean;
}

interface CopiedInterval {
  interval: Interval;
  activityName: string;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
}

interface EditingInterval {
  intervalId: string;
  originalStart: Date;
  originalEnd: Date;
  newStart: Date;
  newEnd: Date;
}

const FullCalendarView: React.FC = () => {
  const { formatMessage } = useIntl();
  const daysIntervals = activityStore(state => state.daysIntervals || []);
  const activityTypes = activityStore(state => state.activityTypes);
  const intervalsParams = activityStore(state => state.intervalsParams);
  const setActivityToEdit = activityStore(state => state.setActivityToEdit);
  const getActivityDetails = activityStore(state => state.getActivityDetails);
  const deleteInterval = activityStore(state => state.deleteInterval);
  const setActivityToCreate = activityStore(state => state.setActivityToCreate);
  const activityToCreate = activityStore(state => state.activityToCreate);
  const rewriteInterval = activityStore(state => state.rewriteInterval);
  const prevActivityToCreateRef = useRef(activityToCreate);

  const [contextMenu, setContextMenu] = useState<ContextMenuState>({ x: 0, y: 0, interval: null });
  const [slotContextMenu, setSlotContextMenu] = useState<SlotContextMenuState>({ x: 0, y: 0, start: null, end: null, gapStart: null, gapEnd: null, hasGap: false, isClick: false, prevEventEnd: null, prevEventName: null, prevEventNearby: false, nextEventStart: null, nextEventName: null, nextEventNearby: false });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [intervalToDelete, setIntervalToDelete] = useState<Interval | null>(null);
  const calendarRef = useRef<FullCalendar>(null);

  // Zoom levels (slot durations in minutes)
  const slotDurations = ['00:15:00', '00:30:00', '01:00:00'];
  const [slotDurationIndex, setSlotDurationIndex] = useState(1); // Default 30min

  // Precision options in minutes
  const precisionOptions = [
    { label: '1m', value: 1 },
    { label: '5m', value: 5 },
    { label: '15m', value: 15 },
  ];
  const [precisionMinutes, setPrecisionMinutes] = useState(5); // Default 5 min
  const [copiedInterval, setCopiedInterval] = useState<CopiedInterval | null>(null);
  const [copyToast, setCopyToast] = useState<string | null>(null);
  const [editingInterval, setEditingInterval] = useState<EditingInterval | null>(null);

  const handleZoomIn = useCallback(() => {
    setSlotDurationIndex(prev => Math.max(0, prev - 1));
  }, []);

  const handleZoomOut = useCallback(() => {
    setSlotDurationIndex(prev => Math.min(slotDurations.length - 1, prev + 1));
  }, []); // slotDurations is a constant, no need to include in deps

  // Format date as YYYY-MM-DD in local timezone (not UTC)
  const formatLocalDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate number of days in selected range
  const { daysInRange, exceedsLimit, actualDays } = useMemo(() => {
    if (intervalsParams.from && intervalsParams.to) {
      const days = differenceInDays(new Date(intervalsParams.to), new Date(intervalsParams.from)) + 1;
      return {
        daysInRange: Math.min(days, MAX_CALENDAR_DAYS),
        exceedsLimit: days > MAX_CALENDAR_DAYS,
        actualDays: days
      };
    }
    return { daysInRange: 7, exceedsLimit: false, actualDays: 7 };
  }, [intervalsParams.from, intervalsParams.to]);

  // Pre-compute activity types map for O(1) lookup
  const activityTypesMap = useMemo(() =>
    new Map(activityTypes?.map(t => [t.id, t]) || []),
    [activityTypes]
  );

  // Convert intervals to calendar events
  const events = useMemo<CalendarEvent[]>(() => {
    const allEvents: CalendarEvent[] = [];

    daysIntervals.forEach(dayInterval => {
      dayInterval.intervals.forEach(interval => {
        const activityType = activityTypesMap.get(interval.typeId);
        const color = convertDecimalToHexColor(activityType?.color || 0);
        const isEditing = editingInterval?.intervalId === interval.id;
        const isDimmed = editingInterval && !isEditing;
        const isOccurrence = !!activityType?.occurrence;

        const startDate = isEditing ? editingInterval.newStart : new Date(interval.start);
        let endDate = isEditing ? editingInterval.newEnd : new Date(interval.finish);

        // Give occurrences a small synthetic duration so they're visible
        if (isOccurrence && startDate.getTime() === endDate.getTime()) {
          endDate = new Date(startDate.getTime() + OCCURRENCE_SYNTHETIC_DURATION_MS);
        }

        const classNames: string[] = [];
        if (isDimmed) classNames.push('fc-event-dimmed');
        if (isEditing) classNames.push('fc-event-editing');
        if (isOccurrence) classNames.push('fc-event-occurrence');

        allEvents.push({
          id: interval.id,
          title: activityType?.name || 'Unknown',
          start: startDate,
          end: endDate,
          backgroundColor: color,
          borderColor: color,
          classNames,
          startEditable: false,            // Dragging disabled for all events
          durationEditable: !isOccurrence, // Resizing only for non-occurrences
          extendedProps: {
            interval,
            isOccurrence,
          },
        });
      });
    });

    return allEvents;
  }, [daysIntervals, activityTypesMap, editingInterval]);

  // Get initial date from params with smart date selection
  const { initialDate, calendarStartDate, calendarEndDate } = useMemo(() => {
    if (!intervalsParams.from || !intervalsParams.to) {
      const today = formatLocalDate(new Date());
      return { initialDate: today, calendarStartDate: today, calendarEndDate: today };
    }

    const rangeStart = new Date(intervalsParams.from);
    const rangeEnd = new Date(intervalsParams.to);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!exceedsLimit) {
      // Range is within limit, show entire range
      return {
        initialDate: intervalsParams.from,
        calendarStartDate: intervalsParams.from,
        calendarEndDate: intervalsParams.to
      };
    }

    // Range exceeds limit - determine which 7 days to show
    let startDate: Date;

    if (today >= rangeStart && today <= rangeEnd) {
      // Today is within the range
      const daysFromStart = differenceInDays(today, rangeStart) + 1;

      if (daysFromStart >= MAX_CALENDAR_DAYS) {
        // Enough days before today - show 7 days ending today
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - (MAX_CALENDAR_DAYS - 1));
      } else {
        // Not enough days before today - show 7 days starting from range start
        startDate = rangeStart;
      }
    } else if (today > rangeEnd) {
      // Today is after the range - show last 7 days
      startDate = new Date(rangeEnd);
      startDate.setDate(startDate.getDate() - (MAX_CALENDAR_DAYS - 1));
    } else {
      // Today is before the range - show first 7 days
      startDate = rangeStart;
    }

    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + (MAX_CALENDAR_DAYS - 1));

    return {
      initialDate: formatLocalDate(startDate),
      calendarStartDate: formatLocalDate(startDate),
      calendarEndDate: formatLocalDate(endDate)
    };
  }, [intervalsParams.from, intervalsParams.to, exceedsLimit]);

  const closeSlotMenu = useCallback((clearSelection = true) => {
    setSlotContextMenu({ x: 0, y: 0, start: null, end: null, gapStart: null, gapEnd: null, hasGap: false, isClick: false, prevEventEnd: null, prevEventName: null, prevEventNearby: false, nextEventStart: null, nextEventName: null, nextEventNearby: false });
    if (clearSelection) {
      calendarRef.current?.getApi().unselect();
    }
  }, []);

  // Handle click on event - show context menu
  const handleEventClick = useCallback((arg: EventClickArg) => {
    arg.jsEvent.stopPropagation();
    closeSlotMenu(); // Close slot menu
    const interval = arg.event.extendedProps?.interval as Interval;

    // Don't show context menu if clicking on the event being edited
    if (editingInterval && interval.id === editingInterval.intervalId) {
      return;
    }

    const pos = adjustMenuPosition(arg.jsEvent.clientX, arg.jsEvent.clientY, 160, 120);
    setContextMenu({
      x: pos.x,
      y: pos.y,
      interval,
    });
  }, [closeSlotMenu, editingInterval]);

  // Handle edit action
  const handleEdit = useCallback(() => {
    if (contextMenu.interval?.activityId) {
      getActivityDetails(contextMenu.interval.activityId, setActivityToEdit);
    }
    setContextMenu({ x: 0, y: 0, interval: null });
  }, [contextMenu.interval, getActivityDetails, setActivityToEdit]);

  // Handle delete action
  const handleDeleteClick = useCallback(() => {
    if (contextMenu.interval) {
      setIntervalToDelete(contextMenu.interval);
      setDeleteDialogOpen(true);
    }
    setContextMenu({ x: 0, y: 0, interval: null });
  }, [contextMenu.interval]);

  const confirmDelete = useCallback(() => {
    if (intervalToDelete) {
      deleteInterval(intervalToDelete);
    }
    setDeleteDialogOpen(false);
    setIntervalToDelete(null);
  }, [intervalToDelete, deleteInterval]);

  // Handle copy action
  const handleCopy = useCallback(() => {
    if (contextMenu.interval) {
      const startDate = new Date(contextMenu.interval.start);
      const endDate = new Date(contextMenu.interval.finish);
      const activityType = contextMenu.interval?.typeId ? activityTypesMap.get(contextMenu.interval.typeId) : undefined;
      const activityName = activityType?.name || 'Unknown';

      setCopiedInterval({
        interval: contextMenu.interval,
        activityName,
        startHour: startDate.getHours(),
        startMinute: startDate.getMinutes(),
        endHour: endDate.getHours(),
        endMinute: endDate.getMinutes(),
      });

      // Show toast notification
      setCopyToast(formatMessage({ id: 'ACTIVITY_COPIED' }, { name: activityName }));
      setTimeout(() => setCopyToast(null), 3000);
    }
    setContextMenu({ x: 0, y: 0, interval: null });
  }, [contextMenu.interval, activityTypesMap, formatMessage]);

  // Handle paste action
  const handlePaste = useCallback((targetDate: Date) => {
    if (!copiedInterval) return;

    const newStart = new Date(targetDate);
    newStart.setHours(copiedInterval.startHour, copiedInterval.startMinute, 0, 0);

    const newEnd = new Date(targetDate);
    newEnd.setHours(copiedInterval.endHour, copiedInterval.endMinute, 0, 0);

    // Handle overnight intervals
    if (newEnd <= newStart) {
      newEnd.setDate(newEnd.getDate() + 1);
    }

    setActivityToCreate({
      status: ActivityStatus.STOPPED,
      typeId: copiedInterval.interval.typeId,
      intervals: [{
        id: '',
        activityId: '',
        typeId: copiedInterval.interval.typeId,
        start: newStart.toISOString(),
        finish: newEnd.toISOString(),
        tags: copiedInterval.interval.tags || [],
        comment: copiedInterval.interval.comment || '',
      }],
      tags: copiedInterval.interval.tags || [],
      comment: copiedInterval.interval.comment || '',
    });

    closeSlotMenu(false); // Keep selection visible while dialog is open
  }, [copiedInterval, setActivityToCreate, closeSlotMenu]);

  // Round time to nearest precision boundary (absolute, not relative)
  const roundToNearestPrecision = useCallback((date: Date): Date => {
    const result = new Date(date);
    const minutes = result.getMinutes();
    const roundedMinutes = Math.round(minutes / precisionMinutes) * precisionMinutes;

    // Handle overflow to next hour
    if (roundedMinutes >= 60) {
      result.setHours(result.getHours() + 1);
      result.setMinutes(roundedMinutes - 60, 0, 0);
    } else {
      result.setMinutes(roundedMinutes, 0, 0);
    }

    return result;
  }, [precisionMinutes]);

  // Handle event resize - enter edit mode and track changes
  const handleEventResize = useCallback((arg: EventResizeDoneArg) => {
    const interval = arg.event.extendedProps?.interval as Interval;
    if (!interval) {
      arg.revert();
      return;
    }

    // Round to nearest absolute precision boundary
    const roundedStart = roundToNearestPrecision(arg.event.start!);
    const roundedEnd = roundToNearestPrecision(arg.event.end!);

    setEditingInterval({
      intervalId: interval.id,
      originalStart: new Date(interval.start),
      originalEnd: new Date(interval.finish),
      newStart: roundedStart,
      newEnd: roundedEnd,
    });
  }, [roundToNearestPrecision]);

  // Save the resized interval
  const handleSaveResize = useCallback(async () => {
    if (!editingInterval) return;

    // Find the original interval from daysIntervals
    let originalInterval: Interval | null = null;
    for (const dayInterval of daysIntervals) {
      const found = dayInterval.intervals.find(i => i.id === editingInterval.intervalId);
      if (found) {
        originalInterval = found;
        break;
      }
    }

    if (!originalInterval) {
      setEditingInterval(null);
      return;
    }

    // Update the interval with new times
    await rewriteInterval({
      ...originalInterval,
      start: editingInterval.newStart.toISOString(),
      finish: editingInterval.newEnd.toISOString(),
    });

    setEditingInterval(null);
  }, [editingInterval, daysIntervals, rewriteInterval]);

  // Cancel the resize and revert
  const handleCancelResize = useCallback(() => {
    setEditingInterval(null);
    // Force calendar re-render to revert visual changes
    calendarRef.current?.getApi().refetchEvents();
  }, []);

  // Track if menu was just closed to prevent immediate re-open
  const menuJustClosedRef = useRef(false);
  // Track if menu was just opened to prevent immediate close from click bubbling
  const menuJustOpenedRef = useRef(false);

  // Handle insert action from slot context menu
  const handleInsertActivity = useCallback((start: Date, end: Date) => {
    setActivityToCreate({
      status: ActivityStatus.STOPPED,
      intervals: [{
        id: '',
        activityId: '',
        typeId: '',
        start: start.toISOString(),
        finish: end.toISOString(),
        tags: [],
        comment: '',
      }],
      tags: [],
      comment: '',
    });
    closeSlotMenu(false); // Keep selection visible while dialog is open
  }, [setActivityToCreate, closeSlotMenu]);

  // Handle selection (both click and drag) - show menu with times
  const handleSelect = useCallback((arg: DateSelectArg) => {
    setContextMenu({ x: 0, y: 0, interval: null });

    // Use FullCalendar's times and round to precision
    let preciseStart = roundToNearestPrecision(arg.start);
    let preciseEnd = roundToNearestPrecision(arg.end);

    // Ensure minimum duration of one precision unit
    if (preciseStart.getTime() >= preciseEnd.getTime()) {
      preciseEnd = new Date(preciseStart.getTime() + precisionMinutes * 60 * 1000);
    }

    // Find nearby events for gap detection
    const dayStart = new Date(preciseStart);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(preciseStart);
    dayEnd.setHours(23, 59, 59, 999);

    const dayEvents = events
      .filter(ev => ev.start >= dayStart && ev.start <= dayEnd)
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    const prevEvent = dayEvents.filter(ev => ev.end <= preciseStart).pop();
    const nextEvent = dayEvents.find(ev => ev.start >= preciseEnd);

    const gapStart = prevEvent ? prevEvent.end : null;
    const gapEnd = nextEvent ? nextEvent.start : null;

    // Check if previous event is within threshold of selection start
    const prevEventNearby = !!prevEvent && (preciseStart.getTime() - prevEvent.end.getTime()) <= NEARBY_THRESHOLD_MS;
    // Check if next event is within threshold of selection end
    const nextEventNearby = !!nextEvent && (nextEvent.start.getTime() - preciseEnd.getTime()) <= NEARBY_THRESHOLD_MS;

    // Show "fill gap" option when both events are nearby and gap extends beyond selection
    const hasGap = prevEventNearby && nextEventNearby &&
      !!gapStart && !!gapEnd &&
      (gapStart.getTime() < preciseStart.getTime() || gapEnd.getTime() > preciseEnd.getTime());

    // Check if this is a click (single precision unit) vs drag (larger selection)
    const selectionDurationMs = preciseEnd.getTime() - preciseStart.getTime();
    const isSingleClick = selectionDurationMs <= precisionMinutes * 60 * 1000;

    // Check if there are extra options (gap filling, nearby events)
    const hasExtraOptions = hasGap || prevEventNearby || nextEventNearby || !!copiedInterval;

    if (!isSingleClick && !hasExtraOptions) {
      // Drag with no extra options - directly open form with selected time range
      handleInsertActivity(preciseStart, preciseEnd);
    } else {
      // Click, or drag with extra options - show menu
      menuJustOpenedRef.current = true;
      setTimeout(() => { menuJustOpenedRef.current = false; }, MENU_TOGGLE_DELAY_MS);
      const pos = adjustMenuPosition(arg.jsEvent?.clientX || 0, arg.jsEvent?.clientY || 0, 200, 200);
      setSlotContextMenu({
        x: pos.x,
        y: pos.y,
        start: preciseStart,
        end: preciseEnd,
        gapStart,
        gapEnd,
        hasGap,
        isClick: isSingleClick,
        prevEventEnd: prevEvent ? prevEvent.end : null,
        prevEventName: prevEvent ? prevEvent.title : null,
        prevEventNearby,
        nextEventStart: nextEvent ? nextEvent.start : null,
        nextEventName: nextEvent ? nextEvent.title : null,
        nextEventNearby,
      });
    }

    // Don't unselect - keep selection visible while menu is open
  }, [events, precisionMinutes, roundToNearestPrecision, copiedInterval, handleInsertActivity]);

  // Close menu when selection starts (before showing new menu)
  const handleSelectAllow = useCallback(() => {
    const hadOpenMenu = !!(slotContextMenu.start || contextMenu.interval);
    closeSlotMenu(false); // Don't clear selection - new one is starting
    setContextMenu({ x: 0, y: 0, interval: null });

    // If a menu was open, just close it and don't allow new selection
    if (hadOpenMenu) {
      menuJustClosedRef.current = true;
      setTimeout(() => { menuJustClosedRef.current = false; }, MENU_TOGGLE_DELAY_MS);
      calendarRef.current?.getApi().unselect(); // Clear old selection
      return false;
    }
    return true;
  }, [closeSlotMenu, slotContextMenu.start, contextMenu.interval]);

  // Cancel edit mode when date range or data changes
  useEffect(() => {
    if (editingInterval) {
      setEditingInterval(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalsParams.from, intervalsParams.to, daysIntervals]);

  // Clear selection when create dialog closes
  useEffect(() => {
    const wasOpen = !!prevActivityToCreateRef.current;
    const isOpen = !!activityToCreate;

    if (wasOpen && !isOpen) {
      // Dialog just closed - clear selection
      calendarRef.current?.getApi().unselect();
    }

    prevActivityToCreateRef.current = activityToCreate;
  }, [activityToCreate]);

  // Key to force re-render when date range changes
  const calendarKey = `${intervalsParams.from}-${intervalsParams.to}`;

  return (
    <div className="fullcalendar-container rounded-lg border border-border/50 bg-card p-4 flex-1 flex flex-col overflow-hidden" onClick={() => {
      if (menuJustOpenedRef.current) return;
      setContextMenu({ x: 0, y: 0, interval: null });
      closeSlotMenu();
    }}>
      {exceedsLimit && (
        <Alert
          variant="warning"
          className="mb-2"
          message={formatMessage(
            { id: 'CALENDAR_LIMIT_WARNING' },
            {
              days: MAX_CALENDAR_DAYS,
              selected: actualDays,
              showingFrom: new Date(calendarStartDate).toLocaleDateString(),
              showingTo: new Date(calendarEndDate).toLocaleDateString()
            }
          )}
        />
      )}

      {/* Edit mode bar - full width above controls */}
      {editingInterval && (
        <EditModeBar
          editingInterval={editingInterval}
          daysIntervals={daysIntervals}
          activityTypesMap={activityTypesMap}
          onCancel={handleCancelResize}
          onSave={handleSaveResize}
          cancelLabel={formatMessage({ id: 'CANCEL' })}
          saveLabel={formatMessage({ id: 'SAVE' })}
        />
      )}

      <div className="flex justify-between items-center gap-3 mb-2">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomIn}
              disabled={slotDurationIndex === 0 || !!editingInterval}
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleZoomOut}
              disabled={slotDurationIndex === slotDurations.length - 1 || !!editingInterval}
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Snap:</span>
            <Segmented
              options={precisionOptions}
              value={precisionMinutes}
              onChange={(value) => setPrecisionMinutes(value as number)}
              disabled={!!editingInterval}
            />
          </div>
        </div>
      </div>
      <FullCalendar
        ref={calendarRef}
        key={calendarKey}
        plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
        initialView="timeGrid"
        initialDate={initialDate}
        duration={{ days: daysInRange }}
        events={events}
        headerToolbar={false}
        height="100%"
        slotMinTime="00:00:00"
        slotMaxTime="24:00:00"
        allDaySlot={false}
        nowIndicator={true}
        dayHeaderFormat={{ month: 'short', day: 'numeric', weekday: 'short' }}
        slotDuration={slotDurations[slotDurationIndex]}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit'
        }}
        firstDay={1}
        selectable={!editingInterval}
        selectMirror={true}
        unselectAuto={false}
        selectAllow={handleSelectAllow}
        eventClick={handleEventClick}
        select={handleSelect}
        editable={true}
        eventStartEditable={false}
        eventDurationEditable={true}
        eventResizableFromStart={true}
        eventResize={handleEventResize}
        snapDuration={`00:${precisionMinutes.toString().padStart(2, '0')}:00`}
      />

      {/* Custom context menu for events */}
      {contextMenu.interval && (
        <EventContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onEdit={handleEdit}
          onCopy={handleCopy}
          onDelete={handleDeleteClick}
          editLabel={formatMessage({ id: 'EDIT' })}
          copyLabel={formatMessage({ id: 'COPY' })}
          deleteLabel={formatMessage({ id: 'DELETE' })}
        />
      )}

      {/* Context menu for empty slots */}
      {slotContextMenu.start && slotContextMenu.end && (
        <SlotContextMenu
          x={slotContextMenu.x}
          y={slotContextMenu.y}
          start={slotContextMenu.start}
          end={slotContextMenu.end}
          gapStart={slotContextMenu.gapStart}
          gapEnd={slotContextMenu.gapEnd}
          hasGap={slotContextMenu.hasGap}
          prevEventEnd={slotContextMenu.prevEventEnd}
          prevEventName={slotContextMenu.prevEventName}
          prevEventNearby={slotContextMenu.prevEventNearby}
          nextEventStart={slotContextMenu.nextEventStart}
          nextEventName={slotContextMenu.nextEventName}
          nextEventNearby={slotContextMenu.nextEventNearby}
          isClick={slotContextMenu.isClick}
          copiedInterval={copiedInterval}
          onInsert={handleInsertActivity}
          onPaste={handlePaste}
          pasteLabel={formatMessage({ id: 'PASTE' })}
        />
      )}

      {/* Copy toast notification */}
      {copyToast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 px-6 py-4 bg-popover border border-border rounded-lg shadow-xl animate-in fade-in zoom-in-95 duration-200">
          <p className="text-base font-medium text-center">{copyToast}</p>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{formatMessage({ id: 'DELETE_INTERVAL' })}</AlertDialogTitle>
            <AlertDialogDescription>
              {formatMessage({ id: 'DELETE_INTERVAL_CONFIRM' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{formatMessage({ id: 'NO' })}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>{formatMessage({ id: 'YES' })}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FullCalendarView;
